import { useEffect, useState } from "react";
import {
  Download, ExternalLink, Instagram, Linkedin, MessageCircle,
  Mail, Music2, Youtube, Facebook, Twitter,
} from "lucide-react";

// ============ METADATA BERSAMA (dipakai editor & halaman publik) ============

export const MICROSITE_THEMES = [
  { value: "purple", label: "Purple", color: "#7c3aed", dark: "#0d0a17" },
  { value: "blue", label: "Blue", color: "#3b82f6", dark: "#070f1e" },
  { value: "green", label: "Green", color: "#10b981", dark: "#051310" },
  { value: "pink", label: "Pink", color: "#ec4899", dark: "#170812" },
  { value: "orange", label: "Orange", color: "#f59e0b", dark: "#151004" },
  { value: "red", label: "Red", color: "#ef4444", dark: "#160809" },
  { value: "teal", label: "Teal", color: "#14b8a6", dark: "#041413" },
  { value: "indigo", label: "Indigo", color: "#6366f1", dark: "#0a0a1c" },
];

export const MICROSITE_TEMPLATES = [
  { value: "classic", label: "Classic", desc: "Avatar atas, tautan bawah" },
  { value: "card", label: "Card", desc: "Cover banner + avatar" },
  { value: "minimal", label: "Minimal", desc: "Bersih, teks saja" },
  { value: "bold", label: "Bold", desc: "Latar penuh, tipografi besar" },
];

export const MICROSITE_BUTTON_STYLES = [
  { value: "solid", label: "Solid" },
  { value: "outline", label: "Garis" },
  { value: "soft", label: "Lembut" },
  { value: "shadow", label: "Bayangan" },
];

export const MICROSITE_BG_MODES = [
  { value: "auto", label: "Ikuti sistem" },
  { value: "gelap", label: "Gelap" },
  { value: "terang", label: "Terang" },
  { value: "gradien", label: "Gradien tema" },
];

export const MICROSITE_FONTS = [
  { value: "jakarta", label: "Plus Jakarta" },
  { value: "grotesk", label: "Space Grotesk" },
  { value: "serif", label: "Serif Klasik" },
];

export const SOCIAL_PLATFORMS = [
  { value: "instagram", label: "Instagram", Icon: Instagram, color: "#ec4899", urlFor: (v) => `https://instagram.com/${v.replace(/^@/, "")}` },
  { value: "linkedin", label: "LinkedIn", Icon: Linkedin, color: "#3b82f6", urlFor: (v) => (/^https?:\/\//.test(v) ? v : `https://linkedin.com/in/${v}`) },
  { value: "whatsapp", label: "WhatsApp", Icon: MessageCircle, color: "#10b981", urlFor: (v) => `https://wa.me/${v.replace(/[^0-9]/g, "")}` },
  { value: "email", label: "Email", Icon: Mail, color: "#f59e0b", urlFor: (v) => `mailto:${v}` },
  { value: "tiktok", label: "TikTok", Icon: Music2, color: "#25181c", urlFor: (v) => `https://tiktok.com/@${v.replace(/^@/, "")}` },
  { value: "youtube", label: "YouTube", Icon: Youtube, color: "#ef4444", urlFor: (v) => (/^https?:\/\//.test(v) ? v : `https://youtube.com/${v}`) },
  { value: "facebook", label: "Facebook", Icon: Facebook, color: "#3b5998", urlFor: (v) => (/^https?:\/\//.test(v) ? v : `https://facebook.com/${v}`) },
  { value: "twitter", label: "X / Twitter", Icon: Twitter, color: "#0f1419", urlFor: (v) => `https://x.com/${v.replace(/^@/, "")}` },
];

export const getMicrositeTheme = (value) => MICROSITE_THEMES.find((t) => t.value === value) || MICROSITE_THEMES[0];
export const getSocialPlatform = (value) => SOCIAL_PLATFORMS.find((p) => p.value === value);

// ============ HELPER GAYA ============

const getButtonLook = (styleId, color, ink, isLight) => {
  switch (styleId) {
    case "solid":
      return { background: color, color: "#fff", border: "1px solid transparent" };
    case "outline":
      return { background: "transparent", color: ink, border: `1.5px solid ${color}` };
    case "shadow":
      return {
        background: isLight ? "#ffffff" : "#15161c",
        color: ink,
        border: `1px solid ${isLight ? "rgba(0,0,0,.1)" : "rgba(255,255,255,.1)"}`,
        boxShadow: `4px 4px 0 ${color}`,
      };
    default: // soft
      return { background: `${color}26`, color: isLight ? color : "#fff", border: `1px solid ${color}3d` };
  }
};

/** Ikuti prefers-color-scheme perangkat pengunjung, dan perbarui otomatis
 * kalau OS-nya berganti skema saat halaman masih terbuka. */
const useSystemDarkMode = () => {
  const [isDark, setIsDark] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : true
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setIsDark(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isDark;
};

const getPageBackground = (bgMode, color, dark) => {
  if (bgMode === "terang") return "#f6f6f4";
  if (bgMode === "gradien") return `radial-gradient(120% 80% at 50% 0%, ${color}4d, ${dark} 62%)`;
  return dark;
};

const getFontFamily = (font) => {
  if (font === "grotesk") return "'Space Grotesk', sans-serif";
  if (font === "serif") return "Georgia, 'Times New Roman', serif";
  return "'Plus Jakarta Sans', sans-serif";
};

const buildVCard = (microsite) => {
  const lines = ["BEGIN:VCARD", "VERSION:3.0", `FN:${microsite.title || ""}`];
  if (microsite.bio) lines.push(`NOTE:${microsite.bio.replace(/\n/g, " ")}`);
  (microsite.social_links || []).forEach((s) => {
    if (s.platform === "email") lines.push(`EMAIL:${s.value}`);
    if (s.platform === "whatsapp") lines.push(`TEL:${s.value}`);
  });
  lines.push("END:VCARD");
  return lines.join("\n");
};

const downloadVCard = (microsite) => {
  const blob = new Blob([buildVCard(microsite)], { type: "text/vcard" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(microsite.title || "kontak").replace(/\s+/g, "-").toLowerCase()}.vcf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ============ SUB-KOMPONEN ============

const VerifiedBadge = ({ color }) => (
  <span
    className="inline-flex items-center justify-center rounded-full shrink-0"
    style={{ width: 18, height: 18, background: color, color: "#fff", fontSize: 10 }}
  >
    ✓
  </span>
);

const SocialRow = ({ socials, isLight, interactive }) => {
  if (!socials?.length) return null;
  return (
    <div className="flex justify-center gap-2.5 flex-wrap mt-3">
      {socials.map((s, i) => {
        const platform = getSocialPlatform(s.platform);
        if (!platform) return null;
        const { Icon } = platform;
        const iconEl = (
          <span
            className="w-9 h-9 rounded-full grid place-items-center shrink-0 transition-transform hover:scale-110"
            style={{
              background: isLight ? "#fff" : "rgba(255,255,255,.08)",
              color: isLight ? platform.color : "#fff",
              boxShadow: `inset 0 0 0 1px ${isLight ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.12)"}`,
            }}
            title={platform.label}
          >
            <Icon className="w-4 h-4" strokeWidth={2.25} />
          </span>
        );
        if (!interactive) return <span key={i}>{iconEl}</span>;
        return (
          <a key={i} href={platform.urlFor(s.value)} target="_blank" rel="noopener noreferrer">
            {iconEl}
          </a>
        );
      })}
    </div>
  );
};

const LinkButton = ({ link, index, buttonLook, buttonStyle, color, radius, bold, interactive, onLinkClick }) => {
  // Badge ikon selalu kontras: putih tipis di atas tombol solid, warna aksen tipis di gaya lain.
  const iconBadgeBg = buttonStyle === "solid" ? "rgba(255,255,255,.18)" : `${color}2e`;
  const content = (
    <div
      className="flex items-center gap-2.5 w-full transition-transform hover:scale-[1.015]"
      style={{
        padding: bold ? "17px 16px" : "14px 15px",
        borderRadius: radius,
        fontSize: bold ? 15 : 13.5,
        fontWeight: 700,
        ...buttonLook,
      }}
    >
      <span
        className="w-7 h-7 rounded-lg grid place-items-center shrink-0 text-[11px]"
        style={{ background: iconBadgeBg }}
      >
        ↗
      </span>
      <span className="flex-1 text-center px-1 truncate">{link.title}</span>
      <ExternalLink className="w-3.5 h-3.5 shrink-0 opacity-50" />
    </div>
  );

  if (!interactive) return <div>{content}</div>;

  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => onLinkClick?.(link, index)}
      className="block"
      data-testid={`microsite-link-${index}`}
    >
      {content}
    </a>
  );
};

const MinimalLinkRow = ({ link, index, ink, subInk, interactive, onLinkClick }) => {
  const content = (
    <div
      className="flex items-center justify-between py-3 border-b transition-all group-hover:pl-1"
      style={{ borderColor: subInk, color: ink }}
    >
      <span className="font-medium truncate pr-2">{link.title}</span>
      <span className="shrink-0 opacity-60">→</span>
    </div>
  );
  if (!interactive) return <div className="group">{content}</div>;
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => onLinkClick?.(link, index)}
      className="block group"
      data-testid={`microsite-link-${index}`}
    >
      {content}
    </a>
  );
};

// ============ RENDERER UTAMA ============

/**
 * Satu-satunya implementasi tampilan microsite — dipakai baik oleh halaman
 * publik (interactive) maupun preview langsung di editor (non-interactive),
 * supaya keduanya dijamin selalu identik pixel-demi-pixel.
 */
const MicrositeRenderer = ({ microsite, interactive = false, onLinkClick, desktopFrame = true }) => {
  const theme = getMicrositeTheme(microsite.theme);
  const color = theme.color;
  const dark = theme.dark;
  const template = microsite.template || "classic";
  const buttonStyle = microsite.button_style || "soft";
  const bgMode = microsite.bg_mode || "gelap";
  const radius = microsite.radius ?? 14;
  const font = getFontFamily(microsite.font);

  const isSystemDark = useSystemDarkMode();
  const effectiveBgMode = bgMode === "auto" ? (isSystemDark ? "gelap" : "terang") : bgMode;

  const hasCustomBg = !!microsite.background_url;
  const isLight = !hasCustomBg && effectiveBgMode === "terang";
  const ink = isLight ? "#141416" : "#f5f5f7";
  const subInk = isLight ? "rgba(0,0,0,.55)" : "rgba(255,255,255,.6)";
  const buttonLook = getButtonLook(buttonStyle, color, ink, isLight);

  const pageBackground = hasCustomBg
    ? `url(${microsite.background_url}) center/cover fixed`
    : getPageBackground(effectiveBgMode, color, dark);

  const bold = template === "bold";
  const card = template === "card";
  const minimal = template === "minimal";
  // Hanya link aktif yang ditampilkan ke pengunjung; yang dinonaktifkan
  // tetap tersimpan (bisa dinyalakan lagi) tapi disembunyikan dari publik.
  const links = (microsite.links || []).filter((l) => l.active !== false);
  const initial = (microsite.title || "M").trim().charAt(0).toUpperCase();

  const avatarSize = bold ? 96 : template === "classic" || card ? 80 : 56;
  const avatarNode = microsite.avatar_url ? (
    <img
      src={microsite.avatar_url}
      alt={microsite.title}
      className="rounded-full object-cover mx-auto"
      style={{
        width: avatarSize,
        height: avatarSize,
        boxShadow: card ? "0 0 0 4px " + (hasCustomBg || !isLight ? "#0d0a17" : "#f6f6f4") : "0 14px 30px -14px rgba(0,0,0,.7)",
        border: card ? "4px solid white" : "none",
      }}
    />
  ) : (
    <div
      className="rounded-full mx-auto grid place-items-center font-extrabold text-white mx-auto"
      style={{
        width: avatarSize,
        height: avatarSize,
        fontSize: avatarSize * 0.38,
        background: `linear-gradient(150deg, ${color}, ${color}99)`,
        boxShadow: card ? "0 0 0 4px white" : "0 14px 30px -14px rgba(0,0,0,.7)",
      }}
    >
      {initial}
    </div>
  );

  const header = (
    <div className={minimal ? "flex items-center gap-4 text-left" : "text-center"}>
      {avatarNode}
      <div className={minimal ? "min-w-0" : "mt-3.5"}>
        <div className={`flex items-center gap-1.5 ${minimal ? "" : "justify-center"}`}>
          <h1
            className="font-extrabold truncate"
            style={{ fontSize: bold ? 23 : minimal ? 18 : 18, color: bold ? "#fff" : ink, letterSpacing: "-0.015em" }}
          >
            {microsite.title || "Nama Anda"}
          </h1>
          {microsite.verified && <VerifiedBadge color={color} />}
        </div>
        {microsite.bio && (
          <p
            className="mt-1.5 leading-relaxed"
            style={{ fontSize: 12.5, color: bold ? "rgba(255,255,255,.8)" : subInk, maxWidth: minimal ? "none" : 300, margin: minimal ? 0 : "6px auto 0" }}
          >
            {microsite.bio}
          </p>
        )}
      </div>
    </div>
  );

  const linkList = (
    <div className="mt-5" style={{ display: "grid", gap: bold ? 12 : 10 }}>
      {links.length === 0 && interactive === false && (
        <div className="text-xs text-center opacity-40 py-4" style={{ color: ink }}>
          Tautan akan muncul di sini
        </div>
      )}
      {minimal
        ? links.map((link, i) => (
            <MinimalLinkRow key={i} link={link} index={i} ink={ink} subInk={isLight ? "rgba(0,0,0,.1)" : "rgba(255,255,255,.12)"} interactive={interactive} onLinkClick={onLinkClick} />
          ))
        : links.map((link, i) => (
            <LinkButton key={i} link={link} index={i} buttonLook={buttonLook} buttonStyle={buttonStyle} color={color} radius={radius} bold={bold} interactive={interactive} onLinkClick={onLinkClick} />
          ))}
    </div>
  );

  const body = (
    <>
        {card && (
          <div
            className="w-full grid place-items-center"
            style={{
              height: 118,
              background: microsite.cover_url
                ? `url(${microsite.cover_url}) center/cover`
                : `repeating-linear-gradient(135deg, rgba(255,255,255,.14) 0 12px, rgba(255,255,255,.05) 12px 24px), ${color}55`,
            }}
          />
        )}
        <div
          className="mx-auto"
          style={{
            maxWidth: 420,
            padding: card ? "0 22px" : "30px 22px 0",
            marginTop: card ? -38 : 0,
          }}
        >
          {header}

          <SocialRow socials={microsite.social_links} isLight={isLight && !hasCustomBg} interactive={interactive} />

          {minimal && <div className="my-4" style={{ borderTop: `1px solid ${isLight ? "rgba(0,0,0,.1)" : "rgba(255,255,255,.12)"}` }} />}

          {linkList}

          {microsite.contact_enabled && (
            <button
              type="button"
              onClick={() => interactive && downloadVCard(microsite)}
              className="inline-flex items-center gap-1.5 mx-auto mt-5 transition-opacity hover:opacity-80"
              style={{
                padding: "9px 17px",
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 700,
                color: ink,
                border: `1px dashed ${isLight ? "rgba(0,0,0,.22)" : "rgba(255,255,255,.24)"}`,
                display: "flex",
              }}
            >
              <Download className="w-3 h-3" /> Simpan kontak
            </button>
          )}

          <div className="text-center mt-6" style={{ fontSize: 10.5, letterSpacing: "0.03em", color: isLight ? "rgba(0,0,0,.42)" : "rgba(255,255,255,.38)" }}>
            Dibuat dengan <b>Campus Link</b>
          </div>
        </div>
    </>
  );

  // Template "bold" sengaja tampil latar-penuh (sesuai deskripsinya), jadi
  // tetap edge-to-edge di layar manapun. Template lain dibungkus jadi satu
  // "kartu" bertepi maksimum yang mengambang di atas latar netral saat
  // layarnya lebih lebar dari lebar HP (sm: ke atas) — tanpa perlakuan ini,
  // cover melebar penuh ke tepi browser sementara konten di bawahnya
  // terpaku sempit, timpang dan terlihat setengah jadi di desktop. Di HP
  // asli (di bawah breakpoint sm) tidak ada bedanya sama sekali.
  const useCardFrame = !bold && desktopFrame;

  const content = hasCustomBg ? (
    <div className="min-h-full" style={{ background: "rgba(0,0,0,.42)", backdropFilter: "blur(1px)" }}>
      {body}
    </div>
  ) : (
    body
  );

  if (!useCardFrame) {
    return (
      <div className="min-h-full" style={{ background: pageBackground, fontFamily: font, color: ink, paddingBottom: 26 }}>
        {content}
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex justify-center" style={{ background: dark }}>
      <div
        className="w-full sm:max-w-[480px] sm:my-10 sm:rounded-[28px] sm:shadow-2xl overflow-hidden"
        style={{ background: pageBackground, fontFamily: font, color: ink, paddingBottom: 26 }}
      >
        {content}
      </div>
    </div>
  );
};

export default MicrositeRenderer;
